import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  Renderer2,
  NgZone,
  HostBinding,
  ChangeDetectorRef,
} from '@angular/core';
import { trigger, state, style, animate, transition } from '@angular/animations';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, filter, map } from 'rxjs';
import { AnimationController, Animation, GestureDetail, GestureController } from '@ionic/angular';
import { RouterStateTrackerService } from 'src/app/services/router-state-tracker.service';

export interface TabConfig {
  route: string;
  iconSvgPath: string;
  id: string;
}

@Component({
  selector: 'tab-bar',
  templateUrl: './tab-bar.component.html',
  styleUrls: ['./tab-bar.component.scss'],
  standalone: false,
  animations: [
    trigger('tabBarVisibility', [
      state('hidden', style({ opacity: 0, transform: 'translateY(100%)', height: '0px', padding: '0' })),
      state('shown', style({ opacity: 1, transform: 'translateY(0%)', height: '15vh', padding: '0' })),
      transition('hidden <=> shown', animate('300ms ease-in-out')),
    ]),
  ],
})
export class TabBarComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('cutoutRef', { static: true }) cutoutRef!: ElementRef<HTMLDivElement>;
  @ViewChild('innerBarRef', { static: true }) innerBarRef!: ElementRef<HTMLDivElement>;
  @ViewChild('cutoutClassRef', { static: true }) cutoutClassRef!: ElementRef<HTMLDivElement>;

  @Input() showOrders: boolean = true;
  @Input() currentActiveRoute: string;
  @Output() tabClick = new EventEmitter<string>();
  @Output() animationStateChanged = new EventEmitter<boolean>();
  dir:boolean=false;
  public cutoutClass: string = '';
  iconColors: string[] = ['tertiary', 'tertiary', 'tertiary'];
  tabs: TabConfig[] = [];
  accountTab: TabConfig = { route: '/accounts', iconSvgPath: '/assets/svg/user.svg', id: 'nav-4' };

  private scrubAnimation!: Animation;
  private routerEventsSub = new Subscription();
  private gestureActive = false;

  private TOTAL_ANIMATION_DISTANCE_PX = 0;
  private currentProgress = 0;
  private gestureStartProgress = 0;
  private readonly DRAG_SENSITIVITY_FACTOR = 0.75;

  constructor(
    private routerStateTracker: RouterStateTrackerService,
    private router: Router,
    private renderer: Renderer2,
    private ngZone: NgZone,
    private animationCtrl: AnimationController,
    private gestureCtrl: GestureController,
    private cd: ChangeDetectorRef,
  ) {}

  ngOnInit() {
  //  this.setTabData();
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        this.setupScrubAnimation();
        this.updateCutoutTransform(this.currentProgress, this.dir);
        this.applyCutoutVisuals(false);
        this.setupGesture();
      }, 0);
    });
  }

  ngAfterViewInit() {
    this.setTabData();
    this.routerEventsSub.add(
      this.router.events
        .pipe(
          filter((e) => e instanceof NavigationEnd),
          map((e: NavigationEnd) => e.urlAfterRedirects.split(/[?#]/)[0]),
        )
        .subscribe((url) => {
          if (this.gestureActive) return;
          const target = this.routeToProgress(url);
          this.moveCutoutToProgress(target);
        }),
    );
    this.animationStateChanged.emit(this.getShouldShowTabBar());
  }

  ngOnDestroy() {
    this.routerEventsSub.unsubscribe();
    try { this.scrubAnimation?.destroy(); } catch {}
  }

  private setTabData() {
    this.tabs = [
      { route: '/calendar', iconSvgPath: '/assets/svg/calendar.svg', id: 'nav-1' },
      { route: '/menu', iconSvgPath: '/assets/svg/menu-n.svg', id: 'nav-2' },
      this.showOrders
        ? { route: '/order', iconSvgPath: '/assets/svg/carts.svg', id: 'nav-3' }
        : { route: '/notifications', iconSvgPath: '/assets/svg/nbell.svg', id: 'nav-3' },
    ];
  
    this.TOTAL_ANIMATION_DISTANCE_PX = this.convertToPixels('48vw');
    
    this.currentProgress = this.routeToProgress(this.currentActiveRoute);
    const idx = this.getIndexFromProgress(this.currentProgress);
    this.iconColors = this.tabs.map((_, i) => (i === idx ? 'selectedIcon' : 'unSelectedIcon'));
  }

  private setupScrubAnimation() {
    this.scrubAnimation = this.animationCtrl
      .create()
      .addElement(this.cutoutRef.nativeElement)
      .duration(1) // dummy duration since we'll drive progress manually
      .keyframes([
      { offset: 0, transform: 'translateX(0px)' },
      { offset: 1, transform: `translateX(${this.TOTAL_ANIMATION_DISTANCE_PX}px)` },
      ]);
    this.scrubAnimation.progressStart(true);
    this.scrubAnimation.progressStep(this.currentProgress);
  }

  private setupGesture() {
    if (!this.innerBarRef) return;
    const gesture = this.gestureCtrl.create({
      el: this.innerBarRef.nativeElement,
      gestureName: 'cutout-swipe',
      threshold: 10,
      passive: true,
      onStart: () => this.onGestureStart(),
      onMove: (ev: GestureDetail) => this.onGestureMove(ev),
      onEnd: (ev: GestureDetail) => this.onGestureEnd(ev),
    });
    gesture.enable(true);
  }

  private onGestureStart() {
    this.gestureActive = true;
    this.gestureStartProgress = this.currentProgress;
    this.scrubAnimation.progressStep(this.currentProgress);
  }

  private onGestureMove(ev: GestureDetail) {
    const delta = (ev.deltaX * this.DRAG_SENSITIVITY_FACTOR) / this.TOTAL_ANIMATION_DISTANCE_PX;
    const newProgress = this.clamp(0, this.gestureStartProgress + delta, 1);
    this.dir = this.currentProgress < newProgress
    this.currentProgress = newProgress;
      let target: number;
  if (this.currentProgress < 0.25) target = 0;
  else if (this.currentProgress < 0.75) target = 0.5;
  else target = 1;
  this.updateIconsForIndex(this.getIndexFromProgress(this.currentProgress));
    this.scrubAnimation.progressStep(newProgress);
    this.updateCutoutTransform(newProgress, this.dir);
    this.applyCutoutVisuals(true);
  }

onGestureEnd(ev: GestureDetail) {
  
  this.gestureActive = false;
  let target: number;
  if (this.currentProgress < 0.25) target = 0;
  else if (this.currentProgress < 0.75) target = 0.5;
  else target = 1;

  const targetRoute = this.progressToRoute(target);
  // if (targetRoute === this.currentActiveRoute) return;
  this.moveCutoutToProgress(target).then(() => {
    this.updateIconsForIndex(this.getIndexFromProgress(target));
    // this.tabClick.emit(targetRoute);
    this.checkEmit(targetRoute)
  });
}
onTabClick(route: string) {
  if (this.gestureActive) return;
  const target = this.routeToProgress(route);
  // if (route === this.currentActiveRoute) return;
// if (target > 1) return;
  this.moveCutoutToProgress(target).then(() => {
    this.updateIconsForIndex(this.getIndexFromProgress(target));
      // this.tabClick.emit(route);
      this.checkEmit(route)
  });
}
checkEmit(route){
  console.log(route, "route");
  console.log(this.currentActiveRoute, "input of route")

  if (route != this.currentActiveRoute) {
    this.tabClick.emit(route);
  }
}
  private moveCutoutToProgress(target: number): Promise<void> {
    const start = this.currentProgress;
    let distance = Math.abs(target - start);
    if (distance === 0) return Promise.resolve();
    // if (distance > 1) {
    //   distance = 1;
    // }

    const duration = Math.max(120, Math.round(distance * 300));
    const startTime = performance.now();

    return new Promise<void>((resolve) => {
      const step = (time: number) => {
        const t = Math.min(1, (time - startTime) / duration);
        const progress = start + (target - start) * t;
        this.currentProgress = progress;
        this.scrubAnimation.progressStep(progress);
        this.applyCutoutVisuals(true);
        if (t < 1) requestAnimationFrame(step);
        else resolve();
      };
      requestAnimationFrame(step);
    });
  }

  private getTransformStyleForProgress(p: number) {
    const tx = p * this.TOTAL_ANIMATION_DISTANCE_PX;
    const sx = (1 + 0.05 * (1 - Math.pow(p * 2 - 1, 2)));
    return `translateX(${tx}px) scaleX(${sx})`;
  }
    private calculateTranslateXFromProgress(p: number) {
    const tx = p * this.TOTAL_ANIMATION_DISTANCE_PX;
    // const sx = (1 + 0.05 * (1 - Math.pow(p * 2 - 1, 2)))*1.5;
    return tx;
  }
// private updateCutoutTransform(progress: number) {
//   const tx = this.calculateTranslateXFromProgress(progress);
//   const swellFactor = 1 - Math.pow(progress * 2 - 1, 3);
//   const sx = 1 + 0.1 * swellFactor;
//   this.renderer.setStyle(this.cutoutClassRef.nativeElement, 'transform', `scaleX(${sx})`);
// }
private updateCutoutTransform(progress: number, direction: boolean) {
  const tx = this.calculateTranslateXFromProgress(progress);

  // Jelly-like scale
  let sx ;
  let rx ;

  // Determine the nearest step points
  const steps = [0, 0.5, 1];
  let prevStep = 0;
  let nextStep = 1;

  for (let i = 0; i < steps.length - 1; i++) {
    if (progress >= steps[i] && progress <= steps[i + 1]) {
      prevStep = steps[i];
      nextStep = steps[i + 1];
      break;
    }
  }

  // progress between the two steps
  const localProgress = (progress - prevStep) / (nextStep - prevStep);
  const rProgress = (nextStep - prevStep) / (progress - prevStep);

  // Apply different curve based on direction
  if (direction) {
    // moving forward: 0 → 1
    const swellFactor = 1 - Math.pow(localProgress * 2 - 1, 2);
    const rFactor = 1 - Math.pow(rProgress * 2 - 1, 2);
    sx = 1 + 0.1 * swellFactor;
    rx = 1 + 0.3 * rFactor;
  } else {
    // moving backward: 1 → 0
    const swellFactor = 1 - Math.pow(localProgress * 2 - 1, 2);
    const rFactor = 1 - Math.pow(rProgress * 2 - 1, 2);
    sx = 1 + 0.3 * swellFactor;
    rx = 1 + 0.1 * rFactor;
  }
  sx = this.clamp(1, sx, 1.5)
  rx = this.clamp(1, rx, 1.2)
  // At exact steps, scale should snap to 1
  if (steps.includes(progress)) {
    sx = 1;
    rx = 1;
  }

  this.renderer.setStyle(this.cutoutClassRef.nativeElement, 'transform', `scaleX(${sx}) scaleY(${rx})`);
}

  private clamp(min: number, n: number, max: number) {
    return Math.max(min, Math.min(n, max));
  }

  private getIndexFromProgress(p: number) {
    return Math.round(p * (this.tabs.length - 1));
  }

  private routeToProgress(route: string) {
    if (route === '/accounts') return 2; // sentinel
    const idx = this.tabs.findIndex((t) => t.route === route);
    return idx === -1 ? 0 : idx / (this.tabs.length - 1);
  }

  private progressToRoute(p: number) {
    if (p === 2) return '/accounts';
    return this.tabs[this.getIndexFromProgress(p)]?.route || '/calendar';
  }

  private applyCutoutVisuals(quick = false) {
    const style = this.cutoutClassRef?.nativeElement?.style;
    if (!style) return;
     this.cutoutClass = "";
    if (this.currentProgress < 0.25) {
    this.cutoutClass = "calendarCut";
    } else if (this.currentProgress < 0.75) {
      this.cutoutClass = "menuCut";
    } else {
      this.cutoutClass = 'orderCut';
    }
    if (!quick) this.cd.detectChanges();
  }

  private updateIconsForIndex(idx: number) {
    this.iconColors = this.tabs.map((_, i) => (i === idx ? 'selectedIcon' : 'unSelectedIcon'));
    this.cd.detectChanges();
  }

  isTabChecked(route: string): boolean {
    return this.currentActiveRoute === route;
  }

  convertToPixels(v: string) {
    const value = parseFloat(v);
    if (v.endsWith('vh')) return (value / 100) * innerHeight;
    if (v.endsWith('vw')) return (value / 100) * innerWidth;
    return value;
  }

  @HostBinding('@tabBarVisibility') get tabBarAnimationState() {
    return this.getShouldShowTabBar() ? 'shown' : 'hidden';
  }

  getShouldShowTabBar(): boolean {
    const valid = this.tabs.map((t) => t.route).concat('/accounts');
    const shouldShow = valid.includes(this.router.url.split(/[?#]/)[0]);
    this.animationStateChanged.emit(shouldShow);
    return shouldShow;
  }

}
